import React, { useMemo } from "react"

import { useForm } from "react-hook-form"

import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"
import { IOneVerifyCoverageResult } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { PopupWrapperElement } from "@front-shared/elements/popupWrapper.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { TextAreaElement } from "@front-shared/elements/textArea.element"
import type { IPopupProps } from "@front-shared/types/props.types"

import { PermissionsHelper } from "../../helpers/permissions.helper"
import { RuntimeHelper } from "../../helpers/runtime.helper"
import { useAppDispatch } from "../../store/appDispatch.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { runtimeSelector } from "../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../store/selectors/selectedPracticeRole.selector"
import { supportReportCoverageResult } from "../../store/thunks/support.thunks"

import arrowRightIcon from "@front-shared/assets/images/icons/arrow-right2.svg"
import closeIcon from "@front-shared/assets/images/icons/close-dark.svg"

export const ReportCoverageCheckResultPopupComponent: React.FunctionComponent<
    IPopupProps & {
        coverageResult: IOneVerifyCoverageResult
    }
> = props => {
    const dispatch = useAppDispatch()

    const {
        handleSubmit,
        formState: { errors },
        control,
    } = useForm()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const runtimeState = useAppSelector(runtimeSelector)

    const canReportCoverageCheckResult = useMemo<boolean>(
        () =>
            PermissionsHelper.hasPermission(
                OneVerifyPermission.reportCoverageCheck,
                selectedPracticeRole,
            ),
        [selectedPracticeRole],
    )

    const isLoading = useMemo<boolean>(() => {
        return RuntimeHelper.isReportCoverageResultLoading()
    }, [runtimeState.isLoading])

    const onSubmitReport = ({ message }: any) => {
        if (!selectedPracticeRole) {
            return
        }

        dispatch(
            supportReportCoverageResult({
                payload: {
                    message,
                    coverageResult: props.coverageResult,
                },
                practice: selectedPracticeRole.practice,
                onSuccess: props.onDone,
            }),
        )
    }

    return (
        <PopupWrapperElement
            isActive={canReportCoverageCheckResult && props.isActive}
            className={props.className}
            onDone={props.onDone}
            closeOnOutsideClick={props.closeOnOutsideClick}
        >
            <div className="flex">
                <PrimaryText typography="h3">
                    What are you flagging?
                </PrimaryText>

                <div
                    className="cursor-pointer mt-[16px]"
                    onClick={props.onDone}
                >
                    <img src={closeIcon} alt="Close" title="Close" />
                </div>
            </div>

            <PrimaryText typography="text" className="mt-[24px]">
                Please explain what looks wrong in result, and our team will
                review your request.
            </PrimaryText>

            <TextAreaElement
                className="mt-[32px]"
                name="message"
                placeholder="Please provide details"
                reactHookControl={control}
                reactHookErrors={errors}
                reactHookValidations={{
                    required: FRONT_VALIDATION_CONFIG.required,
                    minLength: FRONT_VALIDATION_CONFIG.minLength,
                    maxLength: FRONT_VALIDATION_CONFIG.maxLengthText,
                }}
            />

            <div className="mt-[32px] flex">
                <div className="flex-1" />

                <ButtonElement
                    label="Submit"
                    type="primary"
                    size="large"
                    htmlType="button"
                    icon={arrowRightIcon}
                    isRightIcon
                    isLoading={isLoading}
                    onClick={handleSubmit(onSubmitReport)}
                />
            </div>
        </PopupWrapperElement>
    )
}
